import React from 'react';
import { motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';

type LearnedEntry = {
  technology: string;
  priorExperience: string;
  productionReadiness: string;
};

function ThingsLearnedQuickly() {
  const entries = (setup as { quicklyLearned?: LearnedEntry[] }).quicklyLearned || [];
  const quote = (setup as { quicklyLearnedQuote?: string }).quicklyLearnedQuote;

  if (entries.length === 0) return null;

  return (
    <MotionSection id="quickly-learned">
      <SectionHeader
        eyebrow="Adaptability"
        title="Rapid Technology Adoption"
        subtitle="Evidence of how I ramp on unfamiliar technology and ship production systems—not a list of gaps."
      />
      <div className="ui-card overflow-hidden mb-6">
        <div className="hidden sm:grid sm:grid-cols-3 gap-4 px-5 py-3 border-b border-line bg-canvas-muted/50">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">Technology</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">Prior Experience</span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">Production Readiness</span>
        </div>
        <div className="divide-y divide-line">
          {entries.map((entry, index) => (
            <motion.div
              key={entry.technology}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.04 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 px-5 py-4 hover:bg-canvas-muted/30 transition-colors"
            >
              <div>
                <span className="sm:hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted block mb-1">Technology</span>
                <span className="text-sm font-semibold text-ink">{entry.technology}</span>
              </div>
              <div>
                <span className="sm:hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted block mb-1">Prior Experience</span>
                <span className="text-sm text-ink-muted">{entry.priorExperience}</span>
              </div>
              <div>
                <span className="sm:hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted block mb-1">Production Readiness</span>
                <span className="text-sm text-ink-secondary font-medium">{entry.productionReadiness}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      {quote && (
        <blockquote className="ui-card ui-card-lift p-5 sm:p-6 border-l-4 border-l-accent">
          <p className="text-base sm:text-lg text-ink-secondary leading-relaxed italic">
            &ldquo;{quote}&rdquo;
          </p>
        </blockquote>
      )}
    </MotionSection>
  );
}

export default ThingsLearnedQuickly;
